import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { MunicipalitiesService } from '@api/services/municipalities.service';
import { PageHeaderService } from '@ui/app-layout/page-header.service';
import { Tab, TabsComponent } from '@ui/tabs/tabs.component';
import {
  ActivatedRoute,
  Router,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { AuthService } from '@auth/services/auth.service';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [TabsComponent, RouterOutlet],
  template: `<div>
    <ui-tabs
      [tabs]="tabs"
      [activeTab]="activeTab"
      (activeTabChange)="onTabChange($event)"
    ></ui-tabs>
    <router-outlet></router-outlet>
  </div>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsComponent implements OnInit {
  municipalitiesService = inject(MunicipalitiesService);
  pageHeader = inject(PageHeaderService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  destroyRef = inject(DestroyRef);
  authService = inject(AuthService);

  activeTab = 'municipalities';

  tabs: Tab[] = [];

  constructor() {
    this.tabs = this.buildTabs();

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        let childRoute = this.route;
        while (childRoute.firstChild) {
          childRoute = childRoute.firstChild;
        }

        this.activeTab =
          childRoute.snapshot.url[0]?.path ?? this.defaultTabId();
      });
  }

  ngOnInit() {
    this.pageHeader.setTitle('Beheer');

    const current = this.route.snapshot.firstChild?.url[0]?.path;
    if (!current) {
      this.router.navigate([this.defaultTabId()], { relativeTo: this.route });
      return;
    }

    if (!this.tabs.some((tab) => tab.id === current)) {
      this.router.navigate([this.defaultTabId()], { relativeTo: this.route });
    }
  }

  onTabChange(tabId: string) {
    this.activeTab = tabId;
    this.router.navigate([tabId], { relativeTo: this.route });
  }

  private buildTabs(): Tab[] {
    return [
      { id: 'municipalities', label: 'Gemeenten' },
      { id: 'tenders', label: 'Aanbestedingen' },
      { id: 'users', label: 'Gebruikers' },
    ];
  }

  private defaultTabId(): string {
    return 'municipalities';
  }
}
